import { ReactComponent as InfoIcon } from '../../assets/arv/info.svg'
import IncludedExcludedToggle from './IncludedExcludedToggle'

// Tooltip texts are copied literally from the canvas block `Tooltips ARV Analysis`, kept verbatim in
// files/sow2-spec-from-figma.md. The three that shipped were written by the previous developer and
// said different things — "The estimated After-Repair Value derived from adjusted comparable
// sales…", "Measures how closely the selected comps match the subject property…", "Median Days on
// Market measures how long comparable properties…". Replacing strings, then, not writing them.
//
// The apostrophes are U+2019 and the dash in "match best" copy is his; do not straighten them into
// ASCII, the point of a literal text is that it is literal.
const TOOLTIPS = {
  arv: 'Your estimated value is based on the adjusted comps included in your analysis. For better accuracy, keep the comps that match best.',
  quality: 'See how closely your included comps match the property. A higher score means a more reliable ARV estimate.',
  dom: 'See how long your included comps typically stayed active before going under contract. Pending time isn\u2019t included.',
}

function CardHeader({ title, tooltip }) {
  return (
    <div className="flex items-center justify-center gap-x-[8px]">
      {/* ARV 3: 16/800 on arv-estimate_5/Frame 310_13, _3/Frame 829_3 and _4/Frame 829_4 in
          sow2-section-lean.svg. */}
      <span className="text-[16px] font-extrabold text-brand-dark">{title}</span>
      {tooltip ? (
        <div className="relative group">
          <InfoIcon className="cursor-default" />
          <div
            className="absolute z-20 hidden group-hover:block bottom-full left-1/2 -translate-x-1/2 mb-[8px] bg-white text-brand-dark rounded-[8px] p-[14px] text-[13px] leading-[1.5] shadow-lg pointer-events-none"
            style={{ width: 315 }}
          >
            {tooltip}
          </div>
        </div>
      ) : (
        <InfoIcon />
      )}
    </div>
  )
}

// Comment Set #3, item 2: all three card borders move from the old #DCEBD4 to brand-fieldBdr
// (#C6CFC6), the token ArvMap's own border already carries — matches the map on the same screen.
function ArvEstimateCard({ metrics }) {
  const arv   = metrics?.arv      ?? 0
  const count = metrics?.compCount ?? 0
  // The +-4% band is not from any requirement and not from any frame — it was invented here, and on
  // the mock's own numbers it disagrees with the design ($257K against the $256K drawn beside
  // $247K). mr-03 does not replace it: inventing a second unsourced formula to stand where the first
  // one stands is not an improvement. Recorded in files/sow2/spec/arv.md section 8 as an open item
  // to put to Микола, since only he can say what the band is meant to express.
  const low   = Math.round(arv * 0.96)
  const high  = Math.round(arv * 1.04)

  function fmt(n) {
    if (!n) return ''
    const abs = Math.abs(n)
    if (abs >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
    if (abs >= 1_000)     return '$' + Math.round(n / 1_000) + 'K'
    return '$' + n
  }

  return (
    <div className="bg-white border border-brand-fieldBdr rounded-md shadow-card" style={{ width: 300, minHeight: 185, padding: '24px 24px 16px' }}>
      <CardHeader title="ARV Estimate" tooltip={TOOLTIPS.arv} />

      {/* Every dash in this file carries `font-normal` explicitly. `text-brand-placeholder` sets a
          colour and nothing else, and font-weight inherits, so a dash inside a value span takes
          that value's weight — which is how the placeholder ended up rendering at 800 the moment
          the values went ExtraBold, on the 5004 of 5041 properties that have no analysis. The
          same file already shipped this defect once for colour (see the header of
          EstimateMetrics.test.jsx); this is the same mechanism for weight. `columns.js` and
          PropertyDashboard's emptyValueCls hold the same rule: an absent value is not drawn with
          the emphasis of a present one.

          Three dashes are left in this file, not five: ARV 8.1 took the Min and Max pair out on
          02.09. */}
      <div className="flex flex-col gap-[2px] items-center mt-[1rem]">
        <div className="flex items-center ">
          {/* ARV 3.1 (letter 50, paid, "four values that differ"): 34/38 as drawn, 36/36 as asked.
              EstimateMetrics.test.jsx's `arvValue` helper selects by this literal font-size, so
              every test that locates the element through it moved with this number. */}
          <span className="font-extrabold text-brand-dark" style={{ fontSize: 36, lineHeight: '36px' }}>
            {arv ? fmt(arv) : <span className="font-normal text-brand-placeholder">—</span>}
          </span>
        </div>
        {/* The number was missing, not wrong: this label read "Based on adjusted comps" with no
            count at all, while the requirement asks for "Based on 20 adjusted comps". The 20 in his
            text and the 10 in the mock are both mock values — the count is the included comps that
            carry an adjusted price, which is the divisor the ARV above was computed with
            (files/sow2/spec/arv.md section 9, Arv.jsx adjustedComps).

            The singular is not in the Figma string. That mock is drawn at 10, which is a count no
            mock can express the singular of, and "Based on 1 adjusted comps" is reachable the moment
            an analysis has one comp carrying an adjusted price. */}
        { arv ? <span className="text-[12px] text-brand-secondary">Based on <span className="font-semibold">{count}</span> adjusted comp{count === 1 ? '' : 's'}</span> : '' }
      </div>

      {/* Comment Set #1, ARV 8.1: "Remove mini-EM dashes for Min and Max value of ARV Estimate when
          no values are displaying; keep the large EM dash for a cleaner look". The bar and the two
          figures go together — a gradient bar under nothing is the same empty furniture the dashes
          were. The card holds its 185 minHeight, so the sidebar below it does not move when an
          analysis appears.

          The 5004-of-5041 note above still stands for the one dash that remains: an absent value is
          never drawn with the emphasis of a present one. */}
      { arv ? (
        <div className="mt-[.8rem] px-[0px]">
          <div className="h-[8px] rounded-full bg-[linear-gradient(to_right,_#35ce2a,_#1F6F18)] w-full" />
          <div className="flex justify-between mt-[4px]">
            <span className="text-[14px] font-bold text-brand-dark">{fmt(low)}</span>
            <span className="text-[14px] font-bold text-brand-dark">{fmt(high)}</span>
          </div>
        </div>
      ) : null }
    </div>
  )
}

function CompQualityCard({ quality, subtitle }) {
  // No dash here: the subtitle is a descriptive sentence, not a value slot, so an absent one
  // leaves no gap to mark — unlike the label above it, which reads as a missing number if blank.
  const sub = subtitle ?? ''

  return (
    <div className="bg-white rounded-md border border-brand-fieldBdr shadow-card" style={{ width: 300, minHeight: 110, padding: '18px 24px 12px' }}>
      <CardHeader title="Comp Quality" tooltip={TOOLTIPS.quality} />
      <div className="flex flex-col items-center mt-[8px]">
        <span className="text-[28px] font-extrabold text-brand-dark">
          {quality ?? <span className="font-normal text-brand-placeholder">—</span>}
        </span>
        <span className="text-[12px] text-brand-secondary">{sub}</span>
      </div>
    </div>
  )
}

// Average DOM is the arithmetic mean of the included comps' `dom` values, not the median —
// computed in pages/Arv.jsx as `averageDom` via the same `mean` helper the ARV and Comp Quality
// cards use. This card only renders whatever it is handed.
function AverageDomCard({ dom }) {
  return (
    <div className="bg-white rounded-md border border-brand-fieldBdr shadow-card" style={{ width: 300, minHeight: 110, padding: '14px 24px 14px', display:'grid', justifyContent:'center' }}>
      <CardHeader title="Average DOM" tooltip={TOOLTIPS.dom} />
      <div className="flex items-center justify-center mt-[16px]">
        <span className="text-[28px] font-extrabold text-brand-dark">{dom ?? <span className="font-normal text-brand-placeholder">—</span>}</span>
      </div>
    </div>
  )
}

// The toggle is absolutely positioned above this column rather than a flex child of it, so it adds
// no height here — the card column's own top has to stay level with the comp table beside it, and
// a flex child pushes the column down by its own height plus the gap. Of the five props forwarded
// to IncludedExcludedToggle, only `onFiltersBlur` is optional-chained (`onFiltersBlur?.(next)`) and
// `visibleCount`/`hiddenCount` fall back to 0 via `??`. `onFiltersChange` is called bare and is
// required — a caller that omits it gets `TypeError: onFiltersChange is not a function` on click,
// not a graceful no-op. An absent `filters` renders both chips unselected (`archivedView` compared
// against `'active'`/`'archived'` both read false), not an "active" default.
export default function EstimateMetrics({ metrics, filters, onFiltersChange, onFiltersBlur, visibleCount, hiddenCount }) {
  return (
    <div className="relative flex flex-col gap-y-[15px]">
      {/* Same bottom-full + margin idiom CardHeader's own tooltip uses above: out of flow, so it
          costs nothing in this column's height, floating 23px above ArvEstimateCard's top. */}
      <div className="absolute left-0 bottom-full mb-[23px]">
        <IncludedExcludedToggle
          filters={filters}
          onFiltersChange={onFiltersChange}
          onFiltersBlur={onFiltersBlur}
          visibleCount={visibleCount}
          hiddenCount={hiddenCount}
        />
      </div>
      <ArvEstimateCard metrics={metrics} />
      <CompQualityCard
        quality={metrics?.qualityLabel}
        subtitle={metrics?.qualitySub}
      />
      <AverageDomCard dom={metrics?.averageDom} />
    </div>
  )
}
